/* =========================================================================
   Bridge: admin content (id-based graph) → the code-keyed trainee DevPlanState
   the PURE layout engine expects (computeLayout / computeEdges / buildPlanNodes).

   The trainee renderer keys everything on node `code`; the admin API is id-based.
   This is the ONLY place the two shapes meet — it also returns id↔code maps so
   the editor can translate canvas events (which speak `code`) back to API calls
   (which speak `id`). No player progress exists on a draft, so `progress` is [].
   ========================================================================= */

import {
  DevPlanState,
  Tier,
  TrackCode,
} from '../../TraineeDashboard/DevelopmentPlan/types';
import { DevPlanContent } from 'state';

export interface LayoutView {
  /** Feed to buildPlanNodes / computeLayout / computeEdges. */
  state: DevPlanState;
  /** Track color keyed by (canonical) track code — for edges + node accents. */
  trackColor: Record<string, string>;
  /** node.id → display code (translate an API row to a canvas key). */
  codeByNodeId: Record<number, string>;
  /** display code → node.id (translate a canvas selection back to an API id). */
  nodeIdByCode: Record<string, number>;
  /** display codes of is_active=false nodes (rendered dimmed). */
  inactiveCodes: Set<string>;
}

const DUMMY_PROFILE = {
  level: 9999, // draft has no player; keep nothing "level-locked" visually
  total_xp: 0,
  available_skill_points: 0,
  xp_into_level: 0,
  xp_to_next_level: 0,
};

export function adaptContentToLayout(content: DevPlanContent): LayoutView {
  const trackCodeById: Record<number, string> = {};
  content.tracks.forEach((t) => {
    trackCodeById[t.id] = t.code;
  });

  const codeByNodeId: Record<number, string> = {};
  const nodeIdByCode: Record<string, number> = {};
  content.nodes.forEach((n) => {
    codeByNodeId[n.id] = n.code;
    nodeIdByCode[n.code] = n.id;
  });

  const inactiveCodes = new Set<string>();
  content.nodes.forEach((n) => {
    if (!n.is_active) inactiveCodes.add(n.code);
  });

  const tracks = content.tracks
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((t) => ({
      code: t.code as TrackCode,
      name: t.name,
      blurb: t.blurb,
      color: t.color,
      order: t.order,
    }));

  const clusters = content.clusters.map((c) => ({
    track_code: (trackCodeById[c.track] ?? '') as TrackCode,
    code: c.code,
    name: c.name,
    order: c.order,
  }));

  const nodes = content.nodes.map((n) => ({
    code: n.code,
    track_code: (trackCodeById[n.track] ?? '') as TrackCode,
    internal_code: n.internal_code,
    group: n.group,
    tier: n.tier as Tier,
    name: n.name,
    description: n.description,
    meter_name: n.meter_name,
    meter_signed: n.meter_signed,
    required_level: n.required_level,
    skill_points_cost: n.skill_points_cost,
    max_rank: n.max_rank,
    scenario_asset_key: null, // asset keys resolved in the inspector, not needed for layout
    detail: undefined,
  }));

  const edges = content.edges.map((e) => ({
    from: codeByNodeId[e.from_node],
    to: codeByNodeId[e.to_node],
    kind: e.kind,
    required_rank: e.required_rank,
  }));

  const node_games = content.node_games.map((g) => ({
    node_code: codeByNodeId[g.node],
    // game_count keys on node_code, so the exact asset_key is irrelevant here;
    // the inspector resolves real asset titles from the slice's asset pool.
    asset_key: String(g.game_asset),
    role: g.role,
  }));

  const trackColor: Record<string, string> = {};
  tracks.forEach((t) => {
    trackColor[t.code] = t.color;
  });

  const state: DevPlanState = {
    profile: DUMMY_PROFILE,
    tracks,
    clusters,
    nodes,
    edges,
    assets: [],
    node_games,
    progress: [],
  };

  return { state, trackColor, codeByNodeId, nodeIdByCode, inactiveCodes };
}
